import { useQuery } from '@tanstack/react-query'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { rememberTenantSlug, resolveTenantSlug } from '@/consumer/lib/tenant-slug'

/** Tenant-configurable legal content. A `*_url` (hosted) takes precedence over
 *  inline `*_content` when both are set; all keys may be empty strings. */
export interface ConsumerLegalConfig {
  terms_url: string
  privacy_url: string
  terms_content: string
  privacy_content: string
}

/** Organisation locale preferences that drive consumer-app date/currency
 *  formatting. Mirrors the admin `org-context` `preferences` block; the
 *  backend falls back to platform defaults (GB / en / GBP) when unset. */
export interface ConsumerLocaleConfig {
  default_country_code: string
  default_language: string
  default_currency: string
  /** The currencies the org switched on - the only ones a money field may
   *  offer (`OrganisationPreferences.available_currencies`). */
  available_currencies: string[]
}

/** Which product layouts the register step offers, and which it opens on.
 *  Tenant config (`Tenant.settings.consumer.product_picker`), because no two
 *  catalogues have the same shape: a tenant selling one product in many sizes
 *  wants `lines`, a flat catalogue has no lines to group by. The backend
 *  validates both keys and falls back to the platform default, so these are
 *  always a usable pair. */
export interface ConsumerProductPickerConfig {
  views: ('categories' | 'grid' | 'list' | 'lines')[]
  default_view: 'categories' | 'grid' | 'list' | 'lines'
}

export interface ConsumerBranding {
  branding_mode: 'GENERIC' | 'COBRANDED' | 'BRANDED' | 'DISABLED'
  org_name: string | null
  org_logo_url: string | null
  primary_colour: string
  legal: ConsumerLegalConfig
  /** Sparse design-token overrides for this tenant (see shared/lib/theme). */
  theme?: Record<string, string>
  /** Org country/language/currency for locale-aware formatting (DL-54). */
  locale?: ConsumerLocaleConfig
  /** Register-step product layouts for this tenant. */
  product_picker?: ConsumerProductPickerConfig
  /** Consumer-visible capability flags (only the ones the consumer UI needs). */
  feature_flags?: {
    postcode_autofill?: boolean
    /** WPS-138 #1: whether the SSP offers consumer self-registration. When
     *  `false` (a tenant opted out via Tenant.settings, e.g. OC at go-live)
     *  every "Register a product" entry point is hidden. Absent/`true` = on. */
    self_registration?: boolean
    /** Whether customers may raise new claims. `false` hides every claim
     *  entry point. Absent/`true` = on. */
    consumer_claims?: boolean
  }
}

export function useConsumerBranding(overrideSlug?: string) {
  // Effective slug: explicit (route/URL) > build-time env > stored fallback.
  const slug = resolveTenantSlug(overrideSlug)
  return useQuery<ConsumerBranding | null>({
    queryKey: ['consumer-branding', slug],
    queryFn: async () => {
      const qs = slug ? `?tenant=${slug}` : ''
      const res = await fetch(buildApiUrl(`/api/public/consumer-branding/${qs}`))
      const json = await res.json()
      // Persist the explicit slug so future slug-less visits stay on-brand.
      if (overrideSlug) rememberTenantSlug(overrideSlug)
      return json.result ?? null
    },
    staleTime: Infinity,
  })
}
